/**
 * Reused Swedish UI strings. One-off strings stay inline in components; the
 * spec's exact wording is kept verbatim here so every page agrees.
 */
import type { EntityType, InboxFlag, InboxTier, PaymentSourceKind, PrefillReason, TransactionKind, VatPeriod } from '@/domain/types';

/** Common actions (kept flat so the legacy pages' `S.save` etc. keep working). */
export const S = {
  save: 'Spara',
  cancel: 'Avbryt',
  book: 'Bokför',
  activate: 'Aktivera',
  confirm: 'Bekräfta',
  close: 'Stäng',
  back: 'Tillbaka',
  next: 'Fortsätt',
  done: 'Klar',
  edit: 'Ändra',
  skip: 'Hoppa över',
  undo: 'Ångra',
  retry: 'Försök igen',
  remove: 'Ta bort',
  add: 'Lägg till',
  search: 'Sök',
  print: 'Skriv ut / PDF',
  downloadCsv: 'Ladda ner CSV',
  showDetails: 'Visa bokföringsdetaljer',
  hideDetails: 'Dölj bokföringsdetaljer',
  technicalDetails: 'Tekniska detaljer',
  comingSoon: 'Kommer snart',
  loading: 'Laddar…',
  noCompany: 'Välj eller skapa ett företag först.',
  debit: 'Debet',
  credit: 'Kredit',
  account: 'Konto',
  date: 'Datum',
  description: 'Beskrivning',
  amount: 'Belopp',
  category: 'Kategori',
  paidWith: 'Betalt med',
  vat: 'Moms',
  counterparty: 'Motpart',
  receipt: 'Kvitto',
  period: 'Period',
  yes: 'Ja',
  no: 'Nej',
  other: 'Annat…',
} as const;

export const NAV = {
  home: 'Hem',
  inbox: 'Att göra',
  events: 'Händelser',
  reports: 'Rapporter',
  more: 'Mer',
  add: 'Lägg till',
  bookkeeping: 'Bokföring',
  journal: 'Verifikationslista',
  generalLedger: 'Huvudbok',
  trialBalance: 'Råbalans',
  chartOfAccounts: 'Kontoplan',
  eventLog: 'Händelselogg',
  sie: 'SIE-export',
  yearEnd: 'Bokslut',
  newVerifikation: 'Ny verifikation',
} as const;

export const TIER_LABEL: Record<InboxTier, string> = {
  ready: 'Klar att bokföra',
  check: 'Kolla en sak',
  needs_you: 'Behöver dig',
};

/** Group headings in Att göra (plural, take counts). */
export const TIER_GROUP_LABEL: Record<InboxTier, string> = {
  ready: 'Klara att bokföra',
  check: 'Kolla en sak',
  needs_you: 'Behöver dig',
};

export const REASON_LABEL: Record<Exclude<PrefillReason, null>, string> = {
  rule: 'Regel',
  match: 'Matchning',
  memory: 'Minne',
  suggestion: 'Förslag',
};

export const ENTITY_LABEL: Record<EntityType, string> = {
  aktiebolag: 'Aktiebolag',
  enskild_firma: 'Enskild firma',
  ideell_forening: 'Ideell förening',
  bostadsrattsforening: 'Bostadsrättsförening',
};

export const ENTITY_HINT: Record<EntityType, string> = {
  aktiebolag: 'Bolag med aktiekapital. Årsredovisning till Bolagsverket.',
  enskild_firma: 'Du och företaget är samma person. Deklareras i din NE-bilaga.',
  ideell_forening: 'Medlemsavgifter, bidrag och gåvor. Oftast inte momsregistrerad.',
  bostadsrattsforening: 'Årsavgifter, fastighet, lån och underhåll.',
};

/** Layer-0 labels for transaction kinds; owner kinds differ by entity. */
export function kindLabel(kind: TransactionKind, entity: EntityType): string {
  switch (kind) {
    case 'purchase':
      return 'Köp / utgift';
    case 'sale':
      return 'Försäljning / inkomst';
    case 'transfer':
      return 'Överföring mellan egna konton';
    case 'owner_in':
      return entity === 'enskild_firma' ? 'Egen insättning' : 'Insättning eller utlägg från dig';
    case 'owner_out':
      switch (entity) {
        case 'enskild_firma':
          return 'Eget uttag';
        case 'aktiebolag':
          return 'Privat köp – ska betalas tillbaka';
        case 'ideell_forening':
        case 'bostadsrattsforening':
          return 'Utlägg som ska betalas tillbaka';
        default: {
          const exhaustive: never = entity;
          throw new Error(`Unknown entity type ${String(exhaustive)}`);
        }
      }
    case 'refund':
      return 'Återbetalning';
    case 'tax':
      return 'Skatt och moms';
    case 'settle':
      return 'Betalning av faktura';
    case 'vat_settlement':
      return 'Momsomföring';
    case 'year_end':
      return 'Bokslut';
    case 'other':
      return 'Annat';
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown transaction kind ${String(exhaustive)}`);
    }
  }
}

/** Short verb for a proposal sentence: "[Köp] hos [Clas Ohlson]". */
export const KIND_SHORT: Record<TransactionKind, string> = {
  purchase: 'Köp',
  sale: 'Försäljning',
  transfer: 'Överföring',
  owner_in: 'Egna pengar in',
  owner_out: 'Privat uttag',
  refund: 'Återbetalning',
  tax: 'Skatt',
  settle: 'Betalning',
  vat_settlement: 'Momsomföring',
  year_end: 'Bokslut',
  other: 'Annat',
};

export const PAYMENT_SOURCE_KIND_LABEL: Record<PaymentSourceKind, string> = {
  bank: 'Bankkonto',
  card: 'Kort',
  swish: 'Swish',
  cash: 'Kontanter',
  owner: 'Egna pengar',
  supplier_credit: 'Leverantörsfaktura',
  customer_credit: 'Kundfaktura',
  tax_account: 'Skattekontot',
};

export const VAT_PERIOD_LABEL: Record<VatPeriod, string> = {
  month: 'Varje månad',
  quarter: 'Varje kvartal',
  year: 'En gång om året',
};

export const FLAG_LABEL: Record<InboxFlag, string> = {
  possible_private: 'Kan vara privat',
  foreign_currency: 'Utländsk valuta',
  no_vat_breakdown: 'Ingen momsrad',
  representation: 'Representation',
  asset_candidate: 'Kan vara en inventarie',
  foreign_supplier: 'Utländsk säljare',
  duplicate: 'Möjlig dubblett',
  unreadable: 'Svårläst',
  locked_period: 'Låst period',
  outside_fiscal_year: 'Utanför räkenskapsåret',
  large_amount: 'Stort belopp',
};

/** Home (/hem) sentences. */
export const HOME = {
  allDone: (last: string, when: string) => `Allt är bokfört. Senast ${last}, ${when}.`,
  allDoneNoHistory: 'Allt är bokfört.',
  waiting: (receipts: number, bankRows: number) => {
    const parts: string[] = [];
    if (receipts > 0) parts.push(`${receipts} ${receipts === 1 ? 'kvitto' : 'kvitton'}`);
    if (bankRows > 0) parts.push(`${bankRows} ${bankRows === 1 ? 'bankrad' : 'bankrader'}`);
    return `${parts.join(' och ')} väntar på dig.`;
  },
  welcome: 'Välkommen! Lägg till ditt första kvitto.',
  resultYtd: 'Resultat hittills i år',
  resultYtdHint: 'Intäkter − kostnader',
  bankBalance: 'Pengar på kontot enligt bokföringen',
  vatNow: 'Moms just nu',
  vatPay: (amount: string) => `du ska betala ${amount}`,
  vatRefund: (amount: string) => `du får tillbaka ${amount}`,
  owedToYou: (amount: string) => `Företaget är skyldigt dig ${amount}`,
  recent: 'Senaste händelser',
  notBackedUp: 'Bokföringen är inte säkerhetskopierad.',
  /** The banner offers a choice of backend, so it names none. */
  connectStorage: 'Anslut lagring',
} as const;

export const EMPTY = {
  inbox: 'Allt är bokfört.',
  inboxNextVat: (deadline: string) => `Allt är bokfört. Nästa moms ska in ${deadline}.`,
  events: 'Inga händelser ännu. Ditt första kvitto dyker upp här när det är bokfört.',
  bank: 'Inga kontoutdrag ännu. Ladda ner en CSV från internetbanken och släpp den här.',
  report: 'Inget bokfört under perioden.',
  aiMissing: 'Kvittona väntar på AI.',
  connectAi: 'Anslut AI',
  /** Goes to /att-gora: fill in the receipts that are already waiting. */
  fillManually: 'Fyll i själv',
  /** Goes to /lagg-till/handelse — the same words the add menu uses. */
  typeItIn: 'Skriv in själv',
} as const;

/**
 * The storage backend, whichever it is. The pill and the Mer row say "Lagring"
 * rather than naming one vendor, because the app has two adapters and the user
 * picked one; only the strings that genuinely differ take a provider label.
 */
export const STORAGE = {
  backedUp: (time: string) => `Säkerhetskopierad ${time}`,
  pending: (n: number) => `${n} ${n === 1 ? 'ändring' : 'ändringar'} väntar`,
  /** Drive means an expired Google token; Koofr means a missing or rejected app password. */
  needsSignIn: (provider = 'Google Drive') => (provider === 'Google Drive' ? 'Google-inloggning krävs' : `Inloggning i ${provider} krävs`),
  signIn: 'Logga in',
  disconnected: 'Lagring ej ansluten',
  connect: 'Anslut',
  error: (reason: string) => `Kunde inte spara: ${reason}`,
  /** Two devices booked the same number; the provider page has the choice. */
  conflict: 'Två enheter bokförde samma nummer',
  resolve: 'Visa',
  offline: 'Väntar på nätverk',
  backupNow: 'Säkerhetskopiera nu',
  openFolder: (provider = 'Google Drive') => `Öppna i ${provider}`,
  lawTitle: 'Så säkerhetskopieras bokföringen',
  lawBody: (provider = 'Google Drive') =>
    `Bokföringen sparas på din dator och säkerhetskopieras löpande till ${provider}. Appen bevarar äldre månadskopior och sparar kvittofiler separat. HTML- och SIE-exporter är frivilliga och innehåller inte bilagorna. Under Mer › Export kan du ladda ner en fullständig säkerhetskopia, och under Mer › Lagring läsa om reglerna för förvaring.`,
  history: 'Historik',
} as const;

export const AI = {
  connected: (provider: string) => `AI: ${provider}`,
  notConnected: 'AI ej ansluten',
  providerLabel: { gemini: 'Gemini', openai: 'OpenAI', 'openai-compatible': 'Egen server' } as const,
} as const;

export const ACTIVATE_ACCOUNT = {
  title: 'Nytt konto i din kontoplan',
  body: (categoryLabel: string, number: number, name: string) =>
    `Kategorin ${categoryLabel} använder kontot ${number} ${name}. Det läggs till i din kontoplan och loggas.`,
  bodyNoCategory: (number: number, name: string) => `Kontot ${number} ${name} läggs till i din kontoplan och loggas.`,
  bodyMany: (n: number) => `${n} konton läggs till i din kontoplan och loggas.`,
  confirm: 'Lägg till och bokför',
  confirmOnly: 'Lägg till',
} as const;

export const ADVANCED_CALLOUT = {
  id: 'advanced-mode-offer',
  text: 'Vill du ha bokföringsmenyn framme hela tiden?',
  yes: 'Ja, visa avancerat läge',
  no: 'Nej, jag tittar bara',
} as const;

export const DROP_OVERLAY_TEXT = 'Släpp för att lägga i Att göra';

export const UNDO_BOOKING = {
  toast: (ver: string) => `Bokfört som ${ver}`,
  explain: 'Bokföringen kan inte raderas, men vi bokför en rättelse',
} as const;
